import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowUpRight, ArrowLeft, Clock, Calendar, User } from 'lucide-react';
import api from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';

const T = {
  en: { back: 'All articles', more: 'Keep reading', minRead: 'min read', cta: "Let's talk", ctaDesc: 'Ready to bring this to your bank? ClicksyHub builds the engine, your team reaps the results.', ctaBtn: 'Talk to ClicksyHub' },
  es: { back: 'Todos los artículos', more: 'Sigue leyendo', minRead: 'min de lectura', cta: 'Conversemos', ctaDesc: '¿Listo para llevar esto a tu banco? ClicksyHub construye el motor, tu equipo cosecha los resultados.', ctaBtn: 'Habla con ClicksyHub' },
};

// Very small markdown-like renderer: # / ## headings, **bold**, numbered lists, bullet lists, paragraphs.
function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const blocks = [];
  let buf = [];
  const flush = () => { if (buf.length) { blocks.push(['p', buf.join(' ')]); buf = []; } };
  let listType = null;
  let listItems = [];
  const flushList = () => {
    if (listItems.length) {
      blocks.push([listType === 'ol' ? 'ol' : 'ul', [...listItems]]);
      listItems = [];
      listType = null;
    }
  };
  for (let raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) { flush(); flushList(); continue; }
    if (line.startsWith('## ')) { flush(); flushList(); blocks.push(['h2', line.slice(3).trim()]); continue; }
    if (line.startsWith('# ')) { flush(); flushList(); blocks.push(['h1', line.slice(2).trim()]); continue; }
    const ol = line.match(/^\s*(\d+)\.\s+(.*)/);
    if (ol) { flush(); if (listType && listType !== 'ol') flushList(); listType = 'ol'; listItems.push(ol[2]); continue; }
    const ul = line.match(/^\s*[-*]\s+(.*)/);
    if (ul) { flush(); if (listType && listType !== 'ul') flushList(); listType = 'ul'; listItems.push(ul[1]); continue; }
    flushList();
    buf.push(line);
  }
  flush(); flushList();

  const renderInline = (str) => {
    // bold **text**
    const parts = [];
    const re = /\*\*([^*]+)\*\*/g;
    let last = 0, m;
    let idx = 0;
    while ((m = re.exec(str)) !== null) {
      if (m.index > last) parts.push(<React.Fragment key={idx++}>{str.slice(last, m.index)}</React.Fragment>);
      parts.push(<strong key={idx++}>{m[1]}</strong>);
      last = m.index + m[0].length;
    }
    if (last < str.length) parts.push(<React.Fragment key={idx++}>{str.slice(last)}</React.Fragment>);
    return parts.length ? parts : str;
  };

  return (
    <div className="prose-article">
      {blocks.map((b, i) => {
        if (b[0] === 'h1') return <h2 key={i} className="heading-display text-3xl lg:text-4xl mt-14 mb-4">{b[1]}</h2>;
        if (b[0] === 'h2') return <h3 key={i} className="heading-display text-2xl lg:text-3xl mt-12 mb-3">{b[1]}</h3>;
        if (b[0] === 'p') return <p key={i} className="mt-5 text-[17px] leading-[1.75] text-[#C8BDA7]">{renderInline(b[1])}</p>;
        if (b[0] === 'ul') return <ul key={i} className="mt-5 space-y-2 list-none">{b[1].map((li, j) => <li key={j} className="pl-6 relative text-[17px] leading-[1.75] text-[#C8BDA7] before:content-[''] before:absolute before:left-0 before:top-[14px] before:w-2.5 before:h-px before:bg-[#C6C4CF]">{renderInline(li)}</li>)}</ul>;
        if (b[0] === 'ol') return <ol key={i} className="mt-5 space-y-3 counter-reset">{b[1].map((li, j) => <li key={j} className="pl-10 relative text-[17px] leading-[1.75] text-[#C8BDA7]"><span className="absolute left-0 top-0 text-[#F5F137] font-semibold">{String(j+1).padStart(2,'0')}</span>{renderInline(li)}</li>)}</ol>;
        return null;
      })}
    </div>
  );
}

export default function BlogDetailPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { lang } = useLanguage();
  const t = T[lang] || T.en;
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    api.get(`/public/articles/${slug}`).then((r) => { if (!ignore) setArticle(r.data); }).catch((err) => {
      if (!ignore && err?.response?.status === 404) nav('/blog', { replace: true });
    }).finally(() => { if (!ignore) setLoading(false); });
    api.get('/public/articles').then((r) => { if (!ignore) setRelated((r.data || []).filter(a => a.slug !== slug).slice(0, 2)); });
    window.scrollTo({ top: 0 });
    return () => { ignore = true; };
  }, [slug, nav]);

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' }); } catch { return iso; } };
  const tr = (a) => (a?.translations?.[lang] || a?.translations?.en || {});

  if (loading || !article) return <section className="py-32 container-x text-[#C6C4CF]">…</section>;
  const a = tr(article);
  return (
    <>
      <SEO title={a.seo_title || `${a.title} | ClicksyHub`} description={a.seo_description || a.excerpt} keywords={a.seo_keywords} image={article.image} />
      <section className="pt-24 pb-10 lg:pt-32 lg:pb-14">
        <div className="container-x">
          <Link to="/blog" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#C6C4CF] hover:text-[#F5F137]"><ArrowLeft size={14} /> {t.back}</Link>
        </div>
      </section>
      <article className="pb-20">
        <div className="container-x max-w-[960px]">
          <div className="flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.22em] text-[#C6C4CF]">
            {article.category && <span className="border border-[#C6C4CF] px-2.5 py-1 rounded-full">{article.category}</span>}
            <span className="flex items-center gap-1.5"><Calendar size={12} />{fmtDate(article.date)}</span>
            <span className="flex items-center gap-1.5"><Clock size={12} />{article.reading_minutes} {t.minRead}</span>
          </div>
          <h1 className="heading-display text-[38px] sm:text-[52px] lg:text-[72px] mt-6">{a.title}</h1>
          {a.excerpt && <p className="mt-6 text-xl lg:text-2xl text-[#D7CBB3] leading-[1.55] heading-light" style={{ letterSpacing: '-0.01em' }}>{a.excerpt}</p>}
          {article.author_name && (
            <div className="mt-10 flex items-center gap-3 text-sm text-[#C6C4CF]">
              <span className="w-10 h-10 rounded-full border border-[#C6C4CF] text-[#C6C4CF] flex items-center justify-center"><User size={16} /></span>
              <div>
                <p className="text-[#FAF6EF] font-medium">{article.author_name}</p>
                {article.author_role && <p className="text-xs uppercase tracking-[0.22em] text-[#8A8275]">{article.author_role}</p>}
              </div>
            </div>
          )}
        </div>
        {article.image && (
          <div className="container-x mt-12 max-w-[1200px]">
            <div className="aspect-[16/9] overflow-hidden bg-[#2A2A2A]">
              <img src={article.image} alt={a.title} className="w-full h-full object-cover" loading="eager" />
            </div>
          </div>
        )}
        <div className="container-x mt-14 max-w-[820px]">
          {renderMarkdown(a.content)}
        </div>
      </article>
      <section className="py-16 border-t border-[#3A332A]">
        <div className="container-x max-w-[820px]">
          <div className="border border-[#3A332A] rounded-md p-8 lg:p-12 grain">
            <span className="eyebrow">{t.cta}</span>
            <p className="mt-6 heading-display text-2xl lg:text-3xl heading-light" style={{ letterSpacing: '-0.01em' }}>{t.ctaDesc}</p>
            <Link to="/contact" className="btn-outline mt-8"><span>{t.ctaBtn}</span><span className="arrow"><ArrowUpRight size={14} /></span></Link>
          </div>
        </div>
      </section>
      {related.length > 0 && (
        <section className="py-20 border-t border-[#3A332A]">
          <div className="container-x">
            <span className="eyebrow">{t.more}</span>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
              {related.map((r) => {
                const rt = tr(r);
                return (
                  <Link to={`/blog/${r.slug}`} key={r.slug} className="group">
                    <div className="aspect-[16/9] overflow-hidden bg-[#2A2A2A]">
                      <img src={r.image} alt={rt.title} className="w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" loading="lazy" />
                    </div>
                    <div className="pt-5">
                      <div className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-[#C6C4CF]">
                        <span>{fmtDate(r.date)}</span>
                        <span className="flex items-center gap-1"><Clock size={12} />{r.reading_minutes} {t.minRead}</span>
                      </div>
                      <h3 className="heading-display text-2xl lg:text-3xl mt-3 group-hover:text-[#F5F137] transition-colors">{rt.title}</h3>
                      <p className="mt-3 text-[#C6C4CF] text-[15px] leading-relaxed line-clamp-2">{rt.excerpt}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
